import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';
import { Accordion, Button } from 'react-bootstrap';

import { Property } from '../properties';
import StoryboardContext from 'components/Storyboard/context';
import { INTERACTIVE_CONTENT_TYPES, SLIDE_STORYBOARDS } from 'constants/storyboardType';
import { LinkAction, PageAction, PageSpatialHyperLink, ActionHeader } from '../actions';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_PAGE_ACTION_ENABLED } from 'constants/mixpanelEvents';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';

import styles from '../actions/actions.module.scss';
import propertyStyles from '../properties/properties.module.scss';

const FormSubmitActions = ({ target, editorSession, value, onChange, onCancel }) => {
  const dispatch = useDispatch();
  const storyboardContext = React.useContext(StoryboardContext);
  const isInteractiveContent = INTERACTIVE_CONTENT_TYPES.includes(storyboardContext.type);
  const isSlideStoryboard = SLIDE_STORYBOARDS.includes(storyboardContext.type);
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const showPageAction = !isInteractiveContent && entityType !== 'LIVE_ELEMENT' && !isSlideStoryboard;

  const [state, setState] = useState({
    action: value,
    error: null,
  });

  const actionType = _.get(state.action, 'type');

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      action: value,
    }));
  }, [value]);

  const handleTypeChange = (type) => {
    let action = null;
    if (type !== actionType) {
      action = {
        uid: `action_${shortid()}`,
        type,
        newTab: true,
      };
    }
    handleActionChange(action);
  };

  const handleActionChange = (nextAction) => {
    if (!nextAction) {
      return;
    }

    if (
      (nextAction.type === 'slide' || nextAction.type === 'page') &&
      nextAction.deleted
    ) {
      setState((prev) => ({
        ...prev,
        action: null,
        error: null,
      }));
      onChange(null);
      return;
    }

    setState((prev) => ({
      ...prev,
      action: nextAction.type === 'none' ? null : nextAction,
      error: null,
    }));
  };

  const handleCancel = () => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
    onCancel();
  };

  const handleSaveError = () => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  };

  const handleSave = () => {
    if (actionType === 'slide') {
      const slideValue = _.get(state.action, 'value') || {};
      if (!slideValue.page || !slideValue.slide) {
        setState((prev) => ({
          ...prev,
          error: 'field should not be empty !',
        }));
        return;
      }
    }

    if (actionType !== 'pageAnchor' && actionType !== 'slide') {
      if (
        (state.action && !state.action.value && actionType !== 'none') ||
        (state.action && state.action.value === true)
      ) {
        setState((prev) => ({
          ...prev,
          error: 'field should not be empty !',
        }));
        return;
      }
    }

    setState((prev) => ({
      ...prev,
      error: null,
    }));

    onChange(state.action);
    onCancel();

    if (actionType === 'page') {
      const mixpanelPayload = getStoryboardMixpanelEventPayload(
        storyboardContext.id,
        storyboardContext.name,
        storyboardContext.type,
      );
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_ACTION_ENABLED, mixpanelPayload));
    }
  };

  return (
    <Property.Dialog
      width={250}
      target={`#${target}`}
      title="Add action"
      onCancel={handleCancel}
    >
      <Accordion
        activeKey={actionType}
        defaultActiveKey="none"
        className={styles.actions}
      >
        <div className={styles.action}>
          <ActionHeader eventKey="none" onClick={handleTypeChange}>
            None
          </ActionHeader>
        </div>
        <div className={styles.action}>
          <ActionHeader eventKey="link" onClick={handleTypeChange}>
            Web Address
          </ActionHeader>
          <Accordion.Collapse eventKey="link">
            <LinkAction
              value={state.action}
              onChange={handleActionChange}
              saveError={state.error}
              handleSaveError={handleSaveError}
            />
          </Accordion.Collapse>
        </div>
        {isSlideStoryboard && (
          <div className={styles.action}>
            <ActionHeader eventKey="slide" onClick={handleTypeChange}>
              Slide
            </ActionHeader>
            <Accordion.Collapse eventKey="slide">
              <PageSpatialHyperLink
                value={state.action}
                onChange={handleActionChange}
                saveError={state.error}
                editorSession={editorSession}
              />
            </Accordion.Collapse>
          </div>
        )}
        {showPageAction && (
          <div className={styles.action}>
            <ActionHeader eventKey="page" onClick={handleTypeChange}>
              Page
            </ActionHeader>
            <Accordion.Collapse eventKey="page">
              <PageAction
                value={state.action}
                onChange={handleActionChange}
                saveError={state.error}
              />
            </Accordion.Collapse>
          </div>
        )}
      </Accordion>
      <div className={styles.buttonTray}>
        <Button
          className={cx(styles.save, propertyStyles.footerAction, propertyStyles.confirm)}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </div>
    </Property.Dialog>
  );
};

export default FormSubmitActions;
